'use client';

import { useState, useEffect } from 'react';
import { Copy, Check, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { jsObfuscatorContent } from '../../data/js-obfuscator-content';

export default function JsObfuscator() {

    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [obfuscationLevel, setObfuscationLevel] = useState('medium');

    useEffect(() => {
        const savedInput = localStorage.getItem('jsObfuscatorInput');
        if (savedInput) setInput(savedInput);
    }, []);

    const generateRandomVar = () => {
        return '_0x' + Math.random().toString(16).slice(2, 8);
    };

    const obfuscateJs = () => {

        try {

            if (!input.trim()) {
                setError('Please enter some JavaScript to obfuscate');
                return;
            }

            let obfuscated = input;

            /* Rename variables */
            obfuscated = obfuscated.replace(
                /\b(var|let|const)\s+([a-zA-Z_$][a-zA-Z0-9_$]*)/g,
                (match, type) => `${type} ${generateRandomVar()}`
            );

            /* Encode strings */
            obfuscated = obfuscated.replace(/'([^']*)'/g, (_, str) => {
                const encoded = str
                    .split('')
                    .map((c: string) => '\\x' + c.charCodeAt(0).toString(16))
                    .join('');
                return `"${encoded}"`;
            });

            /* Add wrapper */
            obfuscated = `(function(){${obfuscated}})();`;

            setOutput(obfuscated);
            setError('');

            localStorage.setItem('jsObfuscatorInput', input);

            toast.success('JavaScript obfuscated successfully!');

        } catch (err) {

            setError('Error obfuscating JavaScript');
            setOutput('');

        }
    };

    const copyToClipboard = () => {

        if (!output) return;

        navigator.clipboard.writeText(output).then(() => {

            setCopied(true);
            toast.success('Copied to clipboard!');

            setTimeout(() => setCopied(false), 2000);

        });
    };

    const clearAll = () => {

        setInput('');
        setOutput('');
        setError('');

        localStorage.removeItem('jsObfuscatorInput');
    };

    return (

        <div className="min-h-screen bg-[#F9FAFB] py-10 text-slate-900 dark:bg-slate-950 dark:text-slate-50">

            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">

                {/* Header */}

                <div className="mb-8 text-center">

                    <ToolPageIntro title="JavaScript Obfuscator">
                Obfuscate JavaScript code to protect your intellectual property with our free online tool. Make your code harder to understand while maintaining functionality.
            </ToolPageIntro>

                </div>

                {/* Tool */}

                <div className="space-y-6">

                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">

                        {/* Toolbar */}

                        <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/80">

                            <div className="flex flex-wrap items-center justify-between gap-3">

                                <div>

                                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                                        Obfuscate your JavaScript
                                    </p>

                                    <p className="text-xs text-slate-500">
                                        Paste JS on the left, get obfuscated code on the right.
                                    </p>

                                </div>

                                <div className="flex items-center gap-2">

                                    <select
                                        value={obfuscationLevel}
                                        onChange={(e) => setObfuscationLevel(e.target.value)}
                                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
                                    >

                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>

                                    </select>

                                    <button
                                        onClick={clearAll}
                                        className="rounded-full border px-3 py-1 text-xs"
                                    >
                                        Clear
                                    </button>

                                    <button
                                        onClick={obfuscateJs}
                                        className="rounded-full bg-indigo-600 px-4 py-1.5 text-xs text-white"
                                    >
                                        Obfuscate JS
                                    </button>

                                </div>

                            </div>

                        </div>

                        {/* Editors */}

                        <div className="grid grid-cols-1 lg:grid-cols-2">

                            {/* Input */}

                            <div className="p-4">

                                <h3 className="mb-3 text-sm font-medium">
                                    Input JavaScript
                                </h3>

                                <textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    className="h-96 w-full rounded-lg border p-3 font-mono text-sm"
                                    placeholder="Paste your JavaScript code here..."
                                    spellCheck={false}
                                />

                            </div>

                            {/* Output */}

                            <div className="p-4 bg-slate-50/50 dark:bg-slate-900/50">

                                <div className="mb-3 flex justify-between">

                                    <h3 className="text-sm font-medium">
                                        Obfuscated JavaScript
                                    </h3>

                                    <button
                                        onClick={copyToClipboard}
                                        disabled={!output}
                                        className="flex items-center gap-1 text-xs border px-2 py-1 rounded"
                                    >

                                        {copied ? (
                                            <>
                                                <Check className="h-3 w-3" /> Copied
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="h-3 w-3" /> Copy
                                            </>
                                        )}

                                    </button>

                                </div>

                                <textarea
                                    value={output}
                                    readOnly
                                    className="h-96 w-full rounded-lg border p-3 font-mono text-sm"
                                    placeholder="Your obfuscated JavaScript will appear here..."
                                />

                                {error && (

                                    <div className="mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3">

                                        <AlertCircle className="h-4 w-4 text-red-500" />

                                        <p className="text-sm text-red-700">
                                            {error}
                                        </p>

                                    </div>

                                )}

                            </div>

                        </div>

                    </div>

                    {/* SEO Content */}

                    <InformationalContent
                        title={jsObfuscatorContent.title}
                        description={jsObfuscatorContent.description}
                        content={jsObfuscatorContent.content}
                        faqs={jsObfuscatorContent.faqs}
                        relatedTools={jsObfuscatorContent.relatedTools}
                    />

                </div>

            </div>

        </div>

    );
}